import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { generateValueVariableActivityData, ActivityDataModel } from './ValueVariable.utils'

interface UseValueVariableReturnType {
  equationString: string
  variable: string
  variableValue: string
  formValid: boolean
  answer: string
  handleCheckAnswer: () => void
  onInputChange: (val?: string) => void
  goToNextQuestion: () => void
  inputRef: RefObject<HTMLInputElement>
}

interface UseValueVariableProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
  autofocus: boolean
  answerState: boolean | null
}

function UseValueVariable({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
  autofocus,
  answerState,
}: UseValueVariableProps): UseValueVariableReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [activityData, setActivityData] = useState<ActivityDataModel>({
    answer: 0,
    equationString: '',
    variableValue: 0,
    variable: '',
    maxHistoryLength: 10,
  })
  const [answer, setAnswer] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer('')
    setActivityData(generateValueVariableActivityData(difficultyLevel, taskHistory))
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    onAnswerStateChange(parseFloat(answer) === parseFloat(activityData.answer.toString()))
  }, [onAnswerStateChange, answer, activityData.answer])

  const onInputChange = useCallback((value: string) => {
    setAnswer(value)
  }, [])

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [activityData.equationString, ...prevState].slice(0, activityData.maxHistoryLength))
    onNextQuestionClick()
  }, [onNextQuestionClick, activityData.equationString, activityData.maxHistoryLength])

  return {
    equationString: activityData.equationString,
    variable: activityData.variable,
    variableValue: activityData.variableValue.toString(),
    answer,
    formValid: answer !== '',
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    inputRef,
  }
}

export default UseValueVariable
